import { DotSeparatedList, Text, TextButton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { MuiMarkdown } from "@src/appV2/lib/Markdown/MuiMarkdown";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { useOpenGeoLocation } from "@src/appV2/redesign/OpenGeoLocation/useOpenGeoLocation";
import {
  InterviewStatus,
  type InterviewType,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { Link, useHistory } from "react-router-dom";

import { type PlacementDetail } from "../api/useGetPlacement";
import { useAddInterviewToCalendar } from "../hooks/useAddInterviewToCalendar";
import { getPlacementChatPath } from "../paths";
import { PlacementApplicationStatus } from "../types/fetchPlacements.schema";
import { AddToCalendarCard } from "./AddToCalendarCard";
import { type AlternativeAddressData } from "./PlacementDetailsPage/hooks/useAlternativeAddress";

export interface PlacementApplicationMessageBaseProps {
  interviews: PlacementDetail["interviews"];
  hasPendingInterviewRequests: boolean;
  applicationStatus?: PlacementApplicationStatus;
  workplaceName: string;
  placementId: string;
  workplaceId: string;
  interviewInstructions?: string;
  interviewType?: InterviewType;
  workerId: string;
  placementCandidateId: string;
  alternativeAddressData?: AlternativeAddressData;
}

interface PlacementApplicationMessageProps extends PlacementApplicationMessageBaseProps {
  title: string;
  subtitle?: string;
  hasBookedInterview: boolean;
}

export function PlacementApplicationMessage(props: PlacementApplicationMessageProps) {
  const {
    interviews,
    hasPendingInterviewRequests,
    applicationStatus,
    workplaceName,
    placementId,
    workplaceId,
    interviewInstructions,
    interviewType,
    workerId,
    placementCandidateId,
    alternativeAddressData,
    title,
    subtitle,
    hasBookedInterview,
  } = props;

  const history = useHistory();
  const { addToCalendar, isAddingToCalendar } = useAddInterviewToCalendar();
  const { googleMapsHref } = useOpenGeoLocation({
    location: isDefined(alternativeAddressData)
      ? {
          lat: alternativeAddressData.coordinates.latitude,
          lng: alternativeAddressData.coordinates.longitude,
        }
      : undefined,
    formattedAddress: alternativeAddressData?.formattedAddress,
  });

  if (hasPendingInterviewRequests && applicationStatus === PlacementApplicationStatus.APPLIED) {
    return (
      <Text variant="body2">
        {workplaceName} will get back to you in 3 business days, through your Clipboard Chat. If you
        don&apos;t hear back, you can reach out to them for an update{" "}
        <TextButton
          LinkComponent={Link}
          onClick={() => {
            history.push(
              getPlacementChatPath(
                placementId,
                workplaceId,
                "chat-with-placement-candidate-via-job-request"
              )
            );
          }}
        >
          here
        </TextButton>
        .
      </Text>
    );
  }

  return (
    <Stack spacing={4}>
      <Text semibold variant="body1">
        {title}
      </Text>

      {subtitle && <Text variant="body2">{subtitle}</Text>}

      {hasBookedInterview && isDefined(alternativeAddressData) && (
        <Stack spacing={2}>
          <Text bold variant="body2">
            Interview location
          </Text>
          <Text variant="body2">{alternativeAddressData.formattedAddress}</Text>
          <DotSeparatedList
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Text variant="body2">
              {formatDistanceInMiles(alternativeAddressData.distance, "long")}
            </Text>
            {isDefined(googleMapsHref) && (
              <Box
                onClick={() => {
                  logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
                    action: "interview_location_open_in_maps",
                    placementId,
                    placementCandidateId,
                    source: placementCandidateId ? "jobs" : "bookings",
                    workerId,
                  });

                  window.open(googleMapsHref, "_blank");
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    paddingBottom: 1,
                    borderBottom: (theme) => `1px solid ${theme.palette.border?.primary}`,
                  }}
                >
                  <Text variant="body2">Open in map</Text>
                </Stack>
              </Box>
            )}
          </DotSeparatedList>
        </Stack>
      )}

      {hasBookedInterview &&
        isDefined(interviewInstructions) &&
        interviewInstructions.trim().length > 0 && (
          <Stack spacing={3} sx={{ pt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Text semibold variant="body1">
                Important interview details:
              </Text>
            </Stack>
            <Box sx={{ pt: 1 }}>
              <MuiMarkdown variant="body2" lineHeight="1" listMarginTop={0}>
                {interviewInstructions}
              </MuiMarkdown>
            </Box>
          </Stack>
        )}

      {hasBookedInterview &&
        (() => {
          const bookedInterview = interviews
            .filter((interview) => interview.attributes.status === InterviewStatus.BOOKED)
            .toSorted(
              (a, b) =>
                new Date(b.attributes.createdAt).getTime() -
                new Date(a.attributes.createdAt).getTime()
            )
            .at(0);
          const icsFileUrl = bookedInterview?.meta?.icsFileUrl;
          if (!isDefined(bookedInterview) || !isDefined(icsFileUrl)) {
            return undefined;
          }

          return (
            <AddToCalendarCard
              disabled={isAddingToCalendar}
              onClick={async () => {
                await addToCalendar({
                  placementId,
                  interviewId: bookedInterview.id,
                  interviewType: interviewType ?? bookedInterview.attributes.type,
                  icsFileUrl,
                });
              }}
            />
          );
        })()}
    </Stack>
  );
}
